import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';

/**
 * Helper function to extract URL href
 * @param url - The URL string to validate
 * @returns The valid URL href or null if invalid
 */
export const extractUrlHref = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.href;
  } catch {
    // If URL parsing fails, return null
    return null;
  }
};

/**
 * Helper function to get the first valid base URL from operation or document servers
 * Prioritizes operation-level servers over document-level servers
 *
 * @param operation - The OpenAPI operation object
 * @param document - The OpenAPI document
 * @returns The first valid base URL or null if none found
 */
export const getFirstBaseUrl = (
  operation: OpenAPI.OperationObject,
  document: OpenAPI.Document,
): string | null => {
  // First, check operation-level servers
  if (operation.servers) {
    const operationServer = operation.servers.find((server) => extractUrlHref(server.url) != null);
    if (operationServer) {
      return operationServer.url;
    }
  }

  // Fallback to document-level servers
  if (document.servers) {
    const documentServer = document.servers.find((server) => extractUrlHref(server.url) != null);
    if (documentServer) {
      return documentServer.url;
    }
  }

  return null;
};

/**
 * Helper function to extract domain from URL
 * @param url - The URL string to extract domain from
 * @returns The hostname/domain or null if invalid URL
 */
export const extractDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return null;
  }
};
